// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { equals } from "ramda"
import { RootState } from "app/store"
import {
  TimelineRowSortType,
  TimeBlocksSortOrder,
  PersistedToolbar,
  TimeRowViewFlag,
} from "../../../generated/timeline/toolbar/toolbar_pb"
import * as util from "./viewFlags"
import { CountFilteredTimeRowsResponse } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { Tag } from "../../../generated/legebill/type/tag_pb"
import { TagCategory } from "../../../generated/legebill/type/tag_category_pb"

// Define which flags are mutually exclusive, used to ensure that when a
// flag is triggered, no inconsistent state is reached.
const mutuallyExclusiveFlags: Record<TimeRowViewFlag, TimeRowViewFlag[]> = {
  [TimeRowViewFlag.TAGGED]: [TimeRowViewFlag.UNTAGGED],
  [TimeRowViewFlag.UNTAGGED]: [TimeRowViewFlag.TAGGED],
  [TimeRowViewFlag.ERRORED]: [
    TimeRowViewFlag.TAGGED,
    TimeRowViewFlag.UNTAGGED,
  ],
}

// for external use, when merging filter it should contain all the fields
export type SearchFilter = {
  activities: string[],
  descriptions: string[],
  tags: Tag.AsObject[],
  tagCategories: TagCategory.AsObject[],
}

export type BroadSearchPayload = {
  term: string,
  filter: SearchFilter,
}

// search matches by day (yyyyMMdd)
type SearchMatches = {
  [day: string]: number,
}

// search filters by search term
export type SearchTerms = {
  [term: string]: Partial<SearchFilter>,
}

export const timeMarkerHash = "#time-marker"

export type ToolbarState = {
  viewFlags: TimeRowViewFlag[],
  sortType: TimelineRowSortType,
  searchTerms: SearchTerms,
  searchMatches: SearchMatches,
  timeBlocksSortOrder: TimeBlocksSortOrder,
}

export const initialState: ToolbarState = {
  viewFlags: [],
  sortType: TimelineRowSortType.CHRONOLOGY,
  searchTerms: {},
  searchMatches: {},
  timeBlocksSortOrder: TimeBlocksSortOrder.ASCENDING,
}

export type AddViewFlagPayload = {
  flag: TimeRowViewFlag,
  resetOthers: boolean,
}

export const slice = createSlice({
  name: "toolbar",
  initialState,
  reducers: {
    addViewFlag: (state, action: PayloadAction<AddViewFlagPayload>): void => {
      if (action.payload.resetOthers) {
        state.viewFlags = []
      }

      util.addViewFlag(state.viewFlags, action.payload.flag)

      // Delete the mutually exclusive ones
      mutuallyExclusiveFlags[action.payload.flag].forEach(incompatibleFlag => {
        util.removeViewFlag(state.viewFlags, incompatibleFlag)
      })
    },
    removeViewFlag: (state, action: PayloadAction<TimeRowViewFlag>): void => {
      util.removeViewFlag(state.viewFlags, action.payload)
    },
    resetViewFlags: (state): void => {
      state.viewFlags = initialState.viewFlags
    },
    setSortType: (state, action: PayloadAction<TimelineRowSortType>): void => {
      state.sortType = action.payload
    },
    addSearchActivity: (state, action: PayloadAction<string>): void => {
      state.searchTerms[action.payload] = { activities: [action.payload]}
    },
    addSearchDescription: (state, action: PayloadAction<string>): void => {
      state.searchTerms[action.payload] = { descriptions: [action.payload]}
    },
    addSearchTag: (state, action: PayloadAction<Tag.AsObject>): void => {
      state.searchTerms[action.payload.name] = { tags: [action.payload]}
    },
    addSearchTagCategory: (state, action: PayloadAction<TagCategory.AsObject>): void => {
      state.searchTerms[action.payload.label] = { tagCategories: [action.payload]}
    },
    addBroadSearch: (state, action: PayloadAction<BroadSearchPayload>): void => {
      state.searchTerms[action.payload.term] = action.payload.filter
    },
    addTermsSearch: (state, action: PayloadAction<BroadSearchPayload[]>): void => {
      action.payload.forEach(broadSearch => {
        if (!equals(state.searchTerms[broadSearch.term], broadSearch.filter)) {
          state.searchTerms[broadSearch.term] = broadSearch.filter
        }
      })
    },
    removeSearchTerm: (state, action: PayloadAction<string>): void => {
      delete state.searchTerms[action.payload]
    },
    setTimeBlocksSortOrder: (state, action: PayloadAction<TimeBlocksSortOrder>): void => {
      state.timeBlocksSortOrder = action.payload
    },
    setPersistedToolbar: (state, action: PayloadAction<PersistedToolbar.AsObject>): void => {
      state.sortType = action.payload.sortType
      state.timeBlocksSortOrder = action.payload.sortOrder
      state.viewFlags = action.payload.viewFlagsList
    },
    setSearchMatches: (state, action: PayloadAction<CountFilteredTimeRowsResponse.AsObject>): void => {
      state.searchMatches = Object.fromEntries(action.payload.dayCountsMap)
    },
  },
})

export const {
  addViewFlag,
  removeSearchTerm,
  removeViewFlag,
  resetViewFlags,
  setSortType,
  setSearchMatches,
  addSearchActivity,
  addSearchDescription,
  addSearchTag,
  addSearchTagCategory,
  addBroadSearch,
  addTermsSearch,
  setTimeBlocksSortOrder,
  setPersistedToolbar,
} = slice.actions

export const selectToolbarState = (state: RootState): ToolbarState => state.toolbar
export const selectViewFlags = (state: RootState): TimeRowViewFlag[] => state.toolbar.viewFlags
export const selectSortType = (state: RootState): TimelineRowSortType => state.toolbar.sortType
export const selectSearchTerms: (state: RootState) => string[] = createSelector(
  (state: RootState) => state.toolbar.searchTerms,
  searchTerms => Object.keys(searchTerms)
)
export const selectSearchTermsValues = (state: RootState): SearchTerms => state.toolbar.searchTerms
export const selectTimeBlocksSortOrder = (state: RootState): TimeBlocksSortOrder => state.toolbar.timeBlocksSortOrder
export const selectIsHourBlocksView = (state: RootState): boolean =>
  state.toolbar.sortType === TimelineRowSortType.CHRONOLOGY
  || state.toolbar.sortType === TimelineRowSortType.REVERSE_CHRONOLOGY

export default slice.reducer
